import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { get, post, postFiles, put, patch, del } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import { journalCategoryTableColumns, journalCategoryFormFields } from "../../constants/journalPage";
import FormModal from "../../components/Custom/FormModal/FormModal";
import DeleteModal from "../../components/Custom/DeleteModal/DeleteModal";

const JournalCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCategories, setTotalCategories] = useState(0);
  const [search, setSearch] = useState("");
  const [sessionNo, setSessionNo] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [viewQuestionsModal, setViewQuestionsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [addQuestionModal, setAddQuestionModal] = useState(false);
  const [editQuestionModal, setEditQuestionModal] = useState(false);
  const [deleteQuestionModal, setDeleteQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    title: "",
    question: "",
    answerType: "TEXT",
    order: 0,
  });
  const [statusLoading, setStatusLoading] = useState({});
  const [expandedSessions, setExpandedSessions] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchCategories = async () => {
    console.log("fetchCategories called");
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        limit: rowsPerPage,
        search,
        ...(sessionNo && { sessionNo }),
      });

      console.log("Fetching categories with params:", queryParams.toString());
      const response = await get(`/api/dashboard/journal/categories?${queryParams}`);
      console.log("Categories response:", response);
      
      if (response?.success) {
        console.log("Setting categories:", response.data.categories?.length || 0, "categories");
        setCategories(response.data.categories || []);
        setTotalCategories(response.data.pagination?.totalCategories || 0);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toastMessage("Failed to fetch journal categories", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionsByCategory = async (categoryId) => {
    try {
      const response = await get(`/api/dashboard/journal/categories/${categoryId}/questions`);
      if (response?.success) {
        setQuestions(response.data.questions || []);
        // Update the question count in the categories list locally
        updateCategoryQuestionCount(categoryId, response.data.questions?.length || 0);
      }
    } catch (error) {
      toastMessage("Failed to fetch questions", "error");
    }
  };

  const updateCategoryQuestionCount = (categoryId, questionCount) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category._id === categoryId 
          ? { ...category, questionCount }
          : category
      )
    );
  };

  useEffect(() => {
    fetchCategories();
  }, [page, rowsPerPage, search, sessionNo, refreshKey]);

  const handleSubmit = async (formData, isEditing, id) => {
    setLoading(true);
    try {
      // Handle file upload for icon
      let finalFormData = { ...formData };
      
      // Ensure sessionNo is a number
      if (finalFormData.sessionNo && typeof finalFormData.sessionNo === 'object') {
        finalFormData.sessionNo = finalFormData.sessionNo.value;
      }
      
      // Ensure order is a number
      if (finalFormData.order === "" || finalFormData.order === null || finalFormData.order === undefined) {
        finalFormData.order = 0;
      } else {
        finalFormData.order = parseInt(finalFormData.order);
      }
      
      if (formData.icon instanceof File) {
        const iconFormData = new FormData();
        iconFormData.append("file", formData.icon);
        
        const uploadResponse = await postFiles("/api/app/user/uploadImage", iconFormData);
        if (uploadResponse?.data?.url) {
          finalFormData.icon = uploadResponse.data.url;
        }
      }

      if (isEditing) {
        const response = await put(`/api/dashboard/journal/categories/${id}`, finalFormData);
        if (response?.success) {
          toastMessage("Journal category updated successfully", "success");
          setEditModal(false);
          setEditData({});
          console.log("Refreshing categories after edit...");
          await fetchCategories();
          setRefreshKey(prev => prev + 1);
        }
      } else {
        const response = await post("/api/dashboard/journal/categories", finalFormData);
        if (response?.success) {
          toastMessage("Journal category created successfully", "success");
          console.log("Refreshing categories after create...");
          await fetchCategories();
          setRefreshKey(prev => prev + 1);
          // Small delay to ensure state updates are processed
          setTimeout(() => {
            setIsModalOpen(false);
          }, 100);
        }
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await del(`/api/dashboard/journal/categories/${deleteCategory._id}`);
      if (response?.success) {
        toastMessage("Journal category deleted successfully", "success");
        setDeleteModalOpen(false);
        setDeleteCategory(null);
        await fetchCategories();
        setRefreshKey(prev => prev + 1);
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || "Something went wrong", "error");
    }
  };

  const handleViewQuestions = async (category) => {
    setSelectedCategory(category);
    setViewQuestionsModal(true);
    await fetchQuestionsByCategory(category._id);
  };

  const handleAddQuestion = async (questionData) => {
    try {
      const response = await post(`/api/dashboard/journal/questions`, {
        categoryId: selectedCategory._id,
        title: questionData.title,
        question: questionData.question,
        questionType: questionData.answerType.toLowerCase(),
        order: questionData.order,
        required: true,
      });

      if (response?.success) {
        toastMessage("Question added successfully", "success");
        setAddQuestionModal(false);
        setNewQuestion({
          title: "",
          question: "",
          answerType: "TEXT",
          order: 0,
        });
        // Refresh questions and update question count locally
        await fetchQuestionsByCategory(selectedCategory._id);
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || "Something went wrong", "error");
    }
  };

  const handleEditQuestion = async (questionData) => {
    try {
      console.log('Sending edit data:', {
        title: questionData.title,
        question: questionData.question,
        questionType: questionData.questionType.toLowerCase(),
        order: questionData.order,
        required: questionData.required || true,
      });
      
      const response = await put(`/api/dashboard/journal/questions/${editingQuestion._id}`, {
        title: questionData.title,
        question: questionData.question,
        questionType: questionData.questionType.toLowerCase(),
        order: questionData.order,
        required: questionData.required || true,
      });

      if (response?.success) {
        toastMessage("Question updated successfully", "success");
        setEditQuestionModal(false);
        setEditingQuestion(null);
        // Refresh questions and update question count locally
        await fetchQuestionsByCategory(selectedCategory._id);
      }
    } catch (error) {
      console.error('Edit question error:', error);
      toastMessage(error.response?.data?.message || "Something went wrong", "error");
    }
  };

  const handleDeleteQuestion = async () => {
    try {
      const response = await del(`/api/dashboard/journal/questions/${deletingQuestion._id}`);

      if (response?.success) {
        toastMessage("Question deleted successfully", "success");
        setDeleteQuestionModal(false);
        setDeletingQuestion(null);
        // Refresh questions and update question count locally
        await fetchQuestionsByCategory(selectedCategory._id);
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || "Something went wrong", "error");
    }
  };

  const openEditQuestionModal = (question) => {
    setEditingQuestion(question);
    setEditQuestionModal(true);
  };

  const openDeleteQuestionModal = (question) => {
    setDeletingQuestion(question);
    setDeleteQuestionModal(true);
  };

  const handleToggleStatus = async (category) => {
    console.log('handleToggleStatus called for', category._id);
    try {
      setStatusLoading(prev => ({ ...prev, [category._id]: true }));
      const response = await patch(`/api/dashboard/journal/categories/${category._id}/status`);
      if (response?.success) {
        toastMessage(`Category ${category.active ? 'deactivated' : 'activated'} successfully`, "success");
        await fetchCategories(); // Refresh the list
        setRefreshKey(prev => prev + 1);
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || "Failed to toggle status", "error");
    } finally {
      setStatusLoading(prev => ({ ...prev, [category._id]: false }));
    }
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      setEditModal(true);
      setIsModalOpen(false);
      setEditData(dataForEdit);
    } else if (type === "delete") {
      setDeleteModalOpen(true);
      setDeleteCategory(dataForEdit);
    }
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setEditModal(false);
      setEditData({});
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" gutterBottom>
            Journal Categories
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openModal("add")}
          >
            Add Journal Category
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Search Categories"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title or description"
                size="small"
                sx={{ minWidth: 200 }}
              />
              <TextField
                select
                label="Session Number"
                value={sessionNo}
                onChange={(e) => setSessionNo(e.target.value)}
                size="small"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Sessions</MenuItem>
                {Array.from({ length: 13 }, (_, i) => (
                  <MenuItem key={i} value={i}>
                    Session {i}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </CardContent>
        </Card>

        {/* Categories Table - Grouped by Session */}
        <Card key={refreshKey}>
          <CardContent>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography>Loading...</Typography>
              </Box>
            ) : categories.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography>No journal categories found</Typography>
              </Box>
            ) : (
              <>
                                {/* Summary Stats */}
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  mb: 3, 
                  p: 2, 
                  bgcolor: "grey.100", 
                  borderRadius: 1
                }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Total Categories: {categories.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Across {new Set(categories.map(c => c.sessionNo)).size} sessions
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const allSessions = new Set(categories.map(c => c.sessionNo));
                        const allExpanded = {};
                        allSessions.forEach(session => {
                          allExpanded[session] = true;
                        });
                        setExpandedSessions(allExpanded);
                      }}
                    >
                      Expand All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setExpandedSessions({})}
                    >
                      Collapse All
                    </Button>
                  </Box>
                </Box>
                {/* Group categories by session number */}
                {(() => {
                  const groupedCategories = categories.reduce((groups, category) => {
                    const sessionNo = category.sessionNo;
                    if (!groups[sessionNo]) {
                      groups[sessionNo] = [];
                    }
                    groups[sessionNo].push(category);
                    return groups;
                  }, {});

                  // Sort sessions and categories within each session
                  const sortedSessions = Object.keys(groupedCategories).sort((a, b) => parseInt(a) - parseInt(b));
                  
                  return sortedSessions.map((sessionNo) => {
                    const isExpanded = expandedSessions[sessionNo];
                    const sessionCategories = groupedCategories[sessionNo];
                    
                    return (
                      <Box key={sessionNo} sx={{ mb: 2 }}>
                        {/* Collapsible Session Header */}
                        <Box 
                          sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "space-between",
                            p: 2, 
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: "white",
                            "&:hover": {
                              bgcolor: "#f5f5f5"
                            }
                          }}
                          onClick={() => setExpandedSessions(prev => ({
                            ...prev,
                            [sessionNo]: !prev[sessionNo]
                          }))}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                              Session {sessionNo}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
                              {sessionCategories.length} category{sessionCategories.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            sx={{ color: "text.primary" }}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>

                        {/* Collapsible Categories for this session */}
                        {isExpanded && (
                          <Box sx={{ mt: 1, ml: 2 }}>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Icon</TableCell>
                                    <TableCell>Questions</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {sessionCategories
                                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                                    .map((category) => (
                                    <TableRow key={category._id}>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                          {category.title}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {category.description}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {category.icon ? (
                                            <img 
                                              src={category.icon} 
                                              alt="Category Icon" 
                                              style={{ 
                                                width: "40px", 
                                                height: "40px", 
                                                objectFit: "contain",
                                                borderRadius: "4px"
                                              }} 
                                            />
                                          ) : (
                                            "No icon"
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          {category.questionCount > 0 ? (
                                            <Typography variant="body2">
                                              {category.questionCount} questions
                                            </Typography>
                                          ) : (
                                            <Typography variant="body2" color="textSecondary">
                                              No questions
                                            </Typography>
                                          )}
                                          <IconButton
                                            size="small"
                                            onClick={() => handleViewQuestions(category)}
                                            color="info"
                                          >
                                            <ViewIcon />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={category.active ? "Active" : "Inactive"}
                                          color={category.active ? "success" : "error"}
                                          size="small"
                                          onClick={() => {
                                            console.log('Toggling status for', category._id);
                                            if (!statusLoading[category._id]) handleToggleStatus(category);
                                          }}
                                          sx={{
                                            cursor: statusLoading[category._id] ? "not-allowed" : "pointer",
                                            opacity: statusLoading[category._id] ? 0.6 : 1
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                          <IconButton
                                            size="small"
                                            onClick={() => openModal("edit", category)}
                                            color="primary"
                                          >
                                            <EditIcon />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => openModal("delete", category)}
                                            color="error"
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    );
                  });
                })()}
              </>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Modal */}
        <FormModal
          accept="image/*"
          isOpen={isModalOpen || editModal}
          onClose={() => closeModal(editModal ? "edit" : "add")}
          onSubmit={handleSubmit}
          fields={journalCategoryFormFields}
          header={editModal ? "Edit Journal Category" : "Add Journal Category"}
          initialData={editData}
          isEditing={editModal}
        />

        {/* Delete Modal */}
        <DeleteModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={handleDelete}
          data={deleteCategory}
        />

        {/* View Questions Modal */}
        <Dialog
          open={viewQuestionsModal}
          onClose={() => setViewQuestionsModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">
                Questions for {selectedCategory?.title}
              </Typography>
              <IconButton onClick={() => setViewQuestionsModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {questions.length === 0 ? (
              <Alert severity="info">
                No questions found for this category. Add some questions to get started.
              </Alert>
            ) : (
              <Box>
                {questions.map((question, index) => (
                  <Card key={question._id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {question.title || `Question ${index + 1}`}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => openEditQuestionModal(question)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteQuestionModal(question)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {question.question}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Chip
                          label={question.questionType}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          label={question.required ? "Required" : "Optional"}
                          size="small"
                          color={question.required ? "error" : "default"}
                        />
                        <Chip
                          label={`Order: ${question.order}`}
                          size="small"
                          color="default"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                setAddQuestionModal(true);
              }}
            >
              Add Journal Questions
            </Button>
            <Button onClick={() => setViewQuestionsModal(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Question Modal */}
        <Dialog
          open={addQuestionModal}
          onClose={() => setAddQuestionModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">
                Add Question to {selectedCategory?.title}
              </Typography>
              <IconButton onClick={() => setAddQuestionModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Question Title"
                value={newQuestion.title}
                onChange={(e) => setNewQuestion({ ...newQuestion, title: e.target.value })}
                sx={{ mb: 2 }}
                placeholder="e.g., Daily Mood Check, Energy Level, etc."
              />
              <TextField
                fullWidth
                label="Question"
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Answer Type"
                value={newQuestion.answerType}
                onChange={(e) => setNewQuestion({ ...newQuestion, answerType: e.target.value })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="TEXT">Text</MenuItem>
                <MenuItem value="TEXTAREA">Text Area</MenuItem>
                <MenuItem value="RATING">Rating</MenuItem>
                <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
              </TextField>
              <TextField
                fullWidth
                type="number"
                label="Order"
                value={newQuestion.order}
                onChange={(e) => setNewQuestion({ ...newQuestion, order: parseInt(e.target.value) })}
                sx={{ mb: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddQuestionModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleAddQuestion(newQuestion)}
              disabled={!newQuestion.title.trim() || !newQuestion.question.trim()}
            >
              Add Question
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Question Modal */}
        <Dialog
          open={editQuestionModal}
          onClose={() => {
            setEditQuestionModal(false);
            setEditingQuestion(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">
                Edit Question in {selectedCategory?.title}
              </Typography>
              <IconButton onClick={() => {
                setEditQuestionModal(false);
                setEditingQuestion(null);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {editingQuestion && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Question Title"
                  value={editingQuestion.title}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, title: e.target.value })}
                  sx={{ mb: 2 }}
                  placeholder="e.g., Daily Mood Check, Energy Level, etc."
                />
                <TextField
                  fullWidth
                  label="Question"
                  value={editingQuestion.question}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Answer Type"
                  value={editingQuestion.questionType?.toUpperCase()}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, questionType: e.target.value.toLowerCase() })}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="TEXT">Text</MenuItem>
                  <MenuItem value="TEXTAREA">Text Area</MenuItem>
                  <MenuItem value="RATING">Rating</MenuItem>
                  <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  type="number"
                  label="Order"
                  value={editingQuestion.order}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, order: parseInt(e.target.value) })}
                  sx={{ mb: 2 }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditQuestionModal(false);
              setEditingQuestion(null);
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleEditQuestion(editingQuestion)}
              disabled={!editingQuestion?.title?.trim() || !editingQuestion?.question?.trim()}
            >
              Update Question
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Question Modal */}
        <Dialog
          open={deleteQuestionModal}
          onClose={() => {
            setDeleteQuestionModal(false);
            setDeletingQuestion(null);
          }}
          maxWidth="sm"
        >
          <DialogTitle>
            <Typography variant="h6">
              Delete Question
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the question "{deletingQuestion?.title || "this question"}"?
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDeleteQuestionModal(false);
              setDeletingQuestion(null);
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteQuestion}
            >
              Delete Question
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default JournalCategories; 